import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { IoMdClose } from "react-icons/io";
import Btn from "../shared/button/Btn";
import InputText from "../shared/input/new/InputText";
import SmallSpinner from "../shared/spinners/SmallSpinner";
import {
  getCompanyAddressesForDriverImport,
  importCompanyAddressesToDriver,
  resetCompanyAddressImportList,
} from "../../features/driverAddress/driverAddressSlice";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.68);

  .dialog {
    width: min(900px, 100%);
    max-height: calc(100vh - 40px);
    display: flex;
    flex-direction: column;
    padding: 22px;
    border-radius: 12px;
    color: ${({ theme }) => theme.mainTextColor};
    background: ${({ theme }) => theme.mainBgColor};
  }

  .header, .filters, .actions, .address-row, .address-meta {
    display: flex;
    align-items: center;
  }

  .header { justify-content: space-between; margin-bottom: 20px; }
  .header h3 { margin: 0; }
  .close { cursor: pointer; font-size: 24px; }
  .filters { gap: 15px; margin-bottom: 15px; }
  .filters > * { flex: 1; }

  .list {
    min-height: 220px;
    max-height: 460px;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px;
  }

  .address-row {
    align-items: flex-start;
    gap: 12px;
    padding: 13px;
    border: 1px solid ${({ theme }) => theme.mainBorderColor};
    border-radius: 8px;
    cursor: pointer;
  }

  .address-row.disabled { opacity: 0.58; cursor: not-allowed; }
  .address-body { min-width: 0; flex: 1; }
  .address-title { font-weight: 700; margin-bottom: 5px; }
  .address-text { font-size: 13px; overflow-wrap: anywhere; }
  .address-meta { flex-wrap: wrap; gap: 6px; margin-top: 7px; font-size: 12px; }
  .badge { padding: 3px 7px; border-radius: 999px; color: #fff; background: #777; }
  .badge.imported { background: #568a3e; }
  .empty, .loading { padding: 40px; text-align: center; }
  .load-more { display: flex; justify-content: center; margin-top: 10px; }
  .actions { justify-content: space-between; gap: 10px; margin-top: 20px; }
  .action-buttons { display: flex; gap: 10px; }
  .selection-count { font-size: 13px; font-weight: 600; }

  @media (max-width: 700px) {
    padding: 0;
    .dialog { min-height: 100dvh; max-height: 100dvh; border-radius: 0; }
    .filters, .actions { align-items: stretch; flex-direction: column; }
    .action-buttons { width: 100%; }
    .action-buttons > * { flex: 1; }
  }
`;

const searchableText = (address) =>
  [address?.addressName, address?.addressCity, address?.addressTown, address?.address]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase("tr-TR");

const DriverAddressImportModal = ({ targetOwnerUserId, driverAddresses, onClose }) => {
  const dispatch = useDispatch();
  const {
    companyAddresses,
    companyAddressPage,
    hasMoreCompanyAddresses,
    isLoadingCompanyAddresses,
    isMutating,
  } = useSelector((state) => state.driverAddress);
  const [searchText, setSearchText] = useState("");
  const [tag, setTag] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    dispatch(resetCompanyAddressImportList());
    dispatch(getCompanyAddressesForDriverImport({ page: 1, reset: true }));
    return () => dispatch(resetCompanyAddressImportList());
  }, [dispatch]);

  const importedSourceIds = useMemo(
    () =>
      new Set(
        driverAddresses
          .map((address) => address?.sourceSavedAddressId)
          .filter((id) => id != null)
          .map(String)
      ),
    [driverAddresses]
  );

  const filteredAddresses = useMemo(() => {
    const query = searchText.trim().toLocaleLowerCase("tr-TR");
    if (!query) return companyAddresses;
    return companyAddresses.filter((address) => searchableText(address).includes(query));
  }, [companyAddresses, searchText]);

  const toggleAddress = (address) => {
    if (importedSourceIds.has(String(address.id))) return;
    setSelectedIds((current) =>
      current.includes(address.id)
        ? current.filter((id) => id !== address.id)
        : [...current, address.id]
    );
  };

  const handleSubmit = async () => {
    if (!targetOwnerUserId || selectedIds.length === 0) return;
    try {
      await dispatch(
        importCompanyAddressesToDriver({
          sourceSavedAddressIds: selectedIds,
          targetOwnerUserIds: [targetOwnerUserId],
          tag: tag.trim() || null,
        })
      ).unwrap();
      onClose();
    } catch (_) {
      // Backend mesajı thunk tarafından toast olarak gösterilir.
    }
  };

  return (
    <Overlay onClick={onClose}>
      <div className="dialog" onClick={(event) => event.stopPropagation()}>
        <div className="header">
          <h3>Firma Adreslerinden Aktar</h3>
          <IoMdClose className="close" onClick={onClose} />
        </div>

        <div className="filters">
          <InputText data={searchText} setData={(event) => setSearchText(event.target.value)} inputWidth="100%" labelText="Adres Ara" name="driverAddressImportSearch" />
          <InputText data={tag} setData={(event) => setTag(event.target.value)} inputWidth="100%" labelText="Seçilenlere Etiket (Opsiyonel)" name="driverAddressImportTag" />
        </div>

        <div className="list">
          {isLoadingCompanyAddresses && companyAddresses.length === 0 ? (
            <div className="loading"><SmallSpinner size="30px" color="#fff" bgColor="#E85446" /></div>
          ) : filteredAddresses.length === 0 ? (
            <div className="empty">Aktarılabilecek kayıtlı adres bulunamadı.</div>
          ) : (
            filteredAddresses.map((address) => {
              const alreadyImported = importedSourceIds.has(String(address.id));
              const selected = selectedIds.includes(address.id);
              return (
                <div
                  key={address.id}
                  className={`address-row ${alreadyImported ? "disabled" : ""}`}
                  onClick={() => toggleAddress(address)}
                >
                  <input type="checkbox" checked={alreadyImported || selected} disabled={alreadyImported} readOnly />
                  <div className="address-body">
                    <div className="address-title">{address.addressName || "İsimsiz Adres"}</div>
                    <div className="address-text">
                      {[address.addressTown, address.addressCity].filter(Boolean).join(" / ")}
                      {address.address ? ` — ${address.address}` : ""}
                    </div>
                    <div className="address-meta">
                      {alreadyImported && <span className="badge imported">Zaten aktarılmış</span>}
                      {!alreadyImported && selected && <span className="badge">Seçildi</span>}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {hasMoreCompanyAddresses && !searchText.trim() && (
          <div className="load-more">
            <Btn
              width="180px"
              color="#E85446"
              bgColor="transparent"
              isDisabled={isLoadingCompanyAddresses}
              handleClick={() =>
                dispatch(
                  getCompanyAddressesForDriverImport({
                    page: companyAddressPage + 1,
                    reset: false,
                  })
                )
              }
            >
              {isLoadingCompanyAddresses ? "Yükleniyor..." : "Daha Fazla Göster"}
            </Btn>
          </div>
        )}

        <div className="actions">
          <span className="selection-count">{selectedIds.length} adres seçildi</span>
          <div className="action-buttons">
            <Btn width="130px" color="#E85446" bgColor="transparent" handleClick={onClose} isDisabled={isMutating}>
              Vazgeç
            </Btn>
            <Btn width="190px" bgColor="#E85446" color="#fff" handleClick={handleSubmit} isDisabled={isMutating || selectedIds.length === 0}>
              {isMutating ? <SmallSpinner size="18px" color="#E85446" bgColor="#fff" /> : "Seçilenleri Aktar"}
            </Btn>
          </div>
        </div>
      </div>
    </Overlay>
  );
};

export default DriverAddressImportModal;

